import { api } from "@/lib/apiClient";
import type { HeroDocument, ProjectNote, ProjectNoteReference } from "@/lib/projectOverview";
import {
  getHeroAddresses,
  getSOrdner,
  getSOrdnerSharepointWebUrl,
  listOrdersWithTotal,
  type Order,
  type SOrdnerOut,
} from "@/lib/projects";
import { formatSOrdnerLocation, heroAddressPlaceLabel } from "@/pages/projekte/projectFilterSummary";

export type SOrdnerNote = ProjectNote & { sordner_id?: number };

export interface SOrdnerHeroDocument extends HeroDocument {
  auftrag_id: number;
  auftragsnummer: string;
  hero_project_id?: number | null;
}

export interface SOrdnerOverviewBundle {
  sordner: SOrdnerOut;
  location: string | null;
  sharepointUrl: string | null;
  projectCount: number;
  recentProjects: Order[];
  notes: SOrdnerNote[];
}

export function getSOrdnerNotes(sordnerId: number): Promise<SOrdnerNote[]> {
  return api.get<SOrdnerNote[]>(
    `/sordner/${encodeURIComponent(String(sordnerId))}/notes`,
  );
}

export function postSOrdnerNote(
  sordnerId: number,
  body: { note: string; visibility: string; references: ProjectNoteReference[] },
): Promise<SOrdnerNote> {
  return api.post(`/sordner/${encodeURIComponent(String(sordnerId))}/notes`, body);
}

export function deleteSOrdnerNote(
  sordnerId: number,
  noteId: number | string,
): Promise<unknown> {
  return api.del(
    `/sordner/${encodeURIComponent(String(sordnerId))}/notes/${encodeURIComponent(String(noteId))}`,
  );
}

export function getSOrdnerHeroDocuments(
  sordnerId: number,
): Promise<SOrdnerHeroDocument[]> {
  return api.get<SOrdnerHeroDocument[]>(
    `/sordner/${encodeURIComponent(String(sordnerId))}/hero-documents`,
    { cache: "no-store" },
  );
}

export function setSOrdnerCurrentHeroDocument(
  sordnerId: number,
  heroDocumentId: number | null,
): Promise<SOrdnerOut> {
  return api.patch(`/sordner/${encodeURIComponent(String(sordnerId))}/current-hero-document`, {
    hero_document_id: heroDocumentId,
  });
}

export async function loadSOrdnerOverviewBundle(
  sordnerId: number,
): Promise<SOrdnerOverviewBundle> {
  const sordner = await getSOrdner(sordnerId);

  let heroAddressTitle: string | null = null;
  const heroCustomerId = sordner.hero_customer_id;
  const heroAddressId = sordner.hero_address_id;
  if (heroCustomerId && heroAddressId) {
    try {
      const addresses = await getHeroAddresses(heroCustomerId);
      const match = addresses.find((a) => Number(a.address_id) === Number(heroAddressId));
      heroAddressTitle = match ? heroAddressPlaceLabel(match) : null;
    } catch {
      heroAddressTitle = null;
    }
  }

  const [notes, projectsRes, sharepointUrl] = await Promise.all([
    getSOrdnerNotes(sordnerId).catch(() => []),
    listOrdersWithTotal({
      jahr: "S",
      sordner: sordner.nummer,
      limit: 8,
      offset: 0,
      include_hero_steps: false,
      include_timesheet_numbers: false,
    }).catch(() => ({ orders: [], totalCount: 0 })),
    getSOrdnerSharepointWebUrl(sordnerId).catch(() => null),
  ]);

  return {
    sordner,
    location: formatSOrdnerLocation({ ...sordner, hero_address_title: heroAddressTitle }),
    sharepointUrl,
    projectCount: projectsRes.totalCount ?? projectsRes.orders.length,
    recentProjects: projectsRes.orders,
    notes: Array.isArray(notes) ? notes : [],
  };
}
